import { RouterContext, Content, Footer, TripleAction } from '../../../libs/gs-component-right-rail';
import { IconButton } from '../../IconButton';
import { Body1, Caption } from '@xo-union/tk-ui-typography';
import React, { useContext, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Guest, Props as Shared, Routes } from '.';
import emailToName from '../../../helpers/emailToName';
import { splitFullName } from '../../../helpers/splitFullName';
import { TrackingContext } from '../../../TrackingContext';
import styles from './addGuestEmail.scss';

const extraWidth = 16;

const emailReg = /[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/g;
const schema = Yup.string().email();

interface Props extends Shared {
  onAddGuest: React.Dispatch<React.SetStateAction<Guest[]>>;
}

const AddEmailForm: React.FC<Props> = ({ onClose, onAddGuest }) => {
  const [emails, setEmails] = useState<Map<string, boolean>>(new Map());
  const [value, setValue] = useState('');
  const [width, setWidth] = useState(2);
  const inputEl = useRef<HTMLInputElement>(null);
  const divEl = useRef<HTMLDivElement>(null);
  const { push } = useContext(RouterContext);
  const { track } = useContext(TrackingContext);

  const onChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setWidth(divEl.current!.scrollWidth + extraWidth);
    setValue(target.value);
  };

  const addEmail = async () => {
    const email = value.trim();

    try {
      const val = await schema.validate(email);

      if (val) {
        setEmails(new Map(emails.set(val, true)));
        setValue('');
        setWidth(2);
      }
    } catch (error) {
      setEmails(new Map(emails.set(email, false)));
      setValue('');
      setWidth(2);
    }
  };

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Tab', ' ', ','].includes(e.key)) {
      e.preventDefault();

      await addEmail();
    } else if (e.key === 'Backspace' && !value.trim()) {
      // When pressing backspace on input with no value delete the last item
      const arr = Array.from(emails);
      arr.pop();
      setEmails(new Map(arr));
    }
  };

  const onBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();

    await addEmail();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const paste = e.clipboardData.getData('text');
    const matchedEmails = paste.match(emailReg);

    if (matchedEmails) {
      matchedEmails.map(e => emails.set(e, true));
      setEmails(new Map(emails));
    }
  };

  const onRemoveEmail = (email: string) => {
    emails.delete(email);
    setEmails(new Map(emails));
  };

  const onContainerClick = () => {
    inputEl?.current?.focus();
  };

  const addGuests = () => {
    if (emails.size) {
      const names = Array.from(emails).reduce<Guest[]>((initial, [email, isValid]) => {
        if (isValid) {
          // Since there is always one person added to household leader is set to true
          return [...initial, { email, ...splitFullName(emailToName(email)) }];
        }

        return initial;
      }, []);

      if (names.length) {
        onAddGuest(names);
        push(Routes.editGuest);
        track('WWS Share Interaction', { action: 'bullk add emails' });
      }
    }
  };

  return (
    <>
      <Content className={styles.rightRailStyles}>
        <Body1>Enter your guests’ emails below and we’ll add them to your guest list.</Body1>
        <div data-testid="add-guest-email-modal" className={styles.container} onClick={onContainerClick}>
          {emails.size
            ? Array.from(emails).map(([email, isValid], i) => (
                <span
                  data-testid={`email-pill email-pill-${i}`}
                  key={email}
                  className={`${styles.pillContainer} ${!isValid ? styles.errorPill : ''}`}
                >
                  {email}
                  <IconButton
                    datatestid={`pill-cancel-${i}`}
                    arialabel={`Remove ${email} from guest list`}
                    size="sm"
                    name="close"
                    onClick={() => onRemoveEmail(email)}
                  />
                </span>
              ))
            : null}
          <label className={styles.label} htmlFor="email-input">
            <Caption>Email Addresses</Caption>
          </label>
          <input
            id="email-input"
            ref={inputEl}
            className={styles.input}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            name="emails"
            onChange={onChange}
            autoFocus={true}
            value={value}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onPaste={onPaste}
            style={{ width: `${width}px` }}
          />
          <div ref={divEl} className={styles.hiddenDiv}>
            {value}
          </div>
        </div>
      </Content>
      <Footer>
        <TripleAction
          primaryOnClick={addGuests}
          primaryText="Save and continue"
          secondaryOnClick={onClose}
          secondaryText="Cancel"
        />
      </Footer>
    </>
  );
};

export default AddEmailForm;
